import { createLogger } from '@/core/helpers/logger.helper';
import type { ChatApi } from '@/core/network/api/chat/chat.api';
import type { GamResponse } from '@/core/network/http/httpClient.interface';
import { HandleApiError, HttpError } from '@/core/network/http/httpError';
import type { ChatDto, MessageDto } from './chat.interface';

const logger = createLogger('ChatRepository');

export class ChatRepository {
  private readonly httpAPI: ChatApi;

  constructor(httpAPI: ChatApi) {
    this.httpAPI = httpAPI;
  }

  async getChat(chatId: string): Promise<GamResponse<ChatDto> | null> {
    try {
      const response = await this.httpAPI.getChat(chatId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async createGambitChat(gambitId: string): Promise<GamResponse<ChatDto> | null> {
    try {
      const response = await this.httpAPI.createGambitChat(gambitId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async createFirestoreDoc(id: string): Promise<GamResponse<any> | null> {
    try {
      const response = await this.httpAPI.createFirestoreDoc(id);
      return response.status === 200 ? response.data || { data: response.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async markChatAsRead(chatId: string): Promise<void> {
    await this.httpAPI.markChatAsRead(chatId);
  }

  async getMessages(chatId: string, afterCursor?: string): Promise<GamResponse<MessageDto[]> | null> {
    try {
      const response = await this.httpAPI.getMessages(chatId, afterCursor);
      return response.data?.data ? { data: response.data.data, cursor: response.data.cursor } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async postMessage(chatId: string, body: string): Promise<GamResponse<MessageDto> | null> {
    try {
      const response = await this.httpAPI.postMessage(chatId, body);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async deleteMessage(chatId: string, messageId: string): Promise<GamResponse<MessageDto> | null> {
    try {
      const response = await this.httpAPI.deleteMessage(chatId, messageId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }
}
